/**
 * TargetFrame — R232q21 Phase 2.
 *
 * Sirus 3.3.5a-style target unit frame. Mirrors the layout of PlayerFrame
 * but renders the player's currently *selected* unit (Tab-target / left
 * click). Subscribes to `useTarget` for `targetId` + `totId`, and pulls
 * the matching row from `useNet().players` or `.mobs` (mobs first when
 * the id is prefixed `mob:`).
 *
 *   – 64px portrait (race-tinted placeholder until art lands), bordered
 *     by a coloured ring keyed off the faction-reaction (green / yellow /
 *     red). Empty placeholder is *not* shown when there is no target —
 *     we render nothing at all to keep the HUD clean (WoW behaviour).
 *   – HP bar + power bar (mana/rage/etc.) below the name row.
 *   – Right side: ToT (target-of-target) mini-frame: 32px portrait, tiny
 *     HP bar, 10px name. Hidden when `totId` is null or unresolved.
 *
 * Position: fixed top-left, 320px from the left edge so it sits to the
 * right of the (separately rendered) PlayerFrame which lives at left:16.
 * On mobile it stacks vertically below the player frame instead, scaled
 * to 0.85 to claw back screen real-estate.
 *
 * Inputs:
 *   – Click on empty area  → useTarget.clear()           (untarget)
 *   – Right-click          → useTarget.setMouseover(null) (placeholder for
 *                            "cast on target" macro pipeline; the full
 *                            handler lands in a later task).
 *
 * TODO (later task):
 *   – Wire portrait to actual race/class textures once the art-pack maps
 *     RemotePlayer.classId / MobView.kind → portrait sprite.
 *   – Replace `selfPlayer.side` reaction check with full reputation
 *     lookup once the rep system replicates over Colyseus.
 *   – Hook ToT updates: target-store.totId is currently always null because
 *     the Colyseus subscriber that should refill it after `setTarget`
 *     hasn't been wired up. Once it is, this component renders correctly
 *     with no further changes — it's already reading totId here.
 */

import { useCallback, type CSSProperties, type MouseEvent } from 'react';
import { SirusFrame } from './SirusFrame';
import { useTarget } from '../../state/target-store';
import {
  useNet,
  type RemotePlayer,
  type MobView,
} from '../../state/net-store';
import { useUiSettings } from '../../state/ui-settings-store';

type Reaction = 'friendly' | 'neutral' | 'hostile';

interface TargetView {
  id: string;
  name: string;
  level: number;
  hp: number;
  hpMax: number;
  resource: number;
  resourceMax: number;
  reaction: Reaction;
  classId: string | null;
  isPlayer: boolean;
}

const REACTION_RING: Record<Reaction, string> = {
  friendly: 'hsl(120 60% 45%)',
  neutral: 'hsl(50 90% 50%)',
  hostile: 'hsl(0 70% 45%)',
};

const REACTION_GLOW: Record<Reaction, string> = {
  friendly: '0 0 8px hsl(120 60% 45% / 0.55)',
  neutral: '0 0 8px hsl(50 90% 50% / 0.55)',
  hostile: '0 0 8px hsl(0 70% 45% / 0.55)',
};

const CLASS_TINT: Record<string, string> = {
  warrior: '#8B2A2A',
  paladin: '#B89060',
  ranger: '#3F6B33',
  rogue: '#6B5A2E',
  mage: '#3B6FA8',
  cleric: '#A8A2A0',
  shaman: '#1F4F8A',
  warlock: '#5C357A',
  druid: '#7A4A1F',
};

function prettyMobKind(kind: string): string {
  if (!kind) return 'Существо';
  return kind
    .split(/[-_]/g)
    .filter(Boolean)
    .map((s) => s.charAt(0).toUpperCase() + s.slice(1))
    .join(' ');
}

/** Server-polished Russian name when present; fall back to the kind slug. */
function mobDisplayName(m: MobView): string {
  const dn = (m as { displayName?: string }).displayName;
  return (dn && dn.trim().length > 0) ? dn : prettyMobKind(m.kind);
}

function reactionFor(target: RemotePlayer | MobView, isPlayer: boolean, selfSide: string | null): Reaction {
  if (isPlayer) {
    const side = (target as RemotePlayer).side;
    if (selfSide && side === selfSide) return 'friendly';
    return 'hostile';
  }
  const k = ((target as MobView).kind || '').toLowerCase();
  if (k.includes('critter') || k.includes('ambient') || k.includes('cow') || k.includes('rabbit')) {
    return 'neutral';
  }
  return 'hostile';
}

function resolveTarget(
  id: string | null,
  players: Record<string, RemotePlayer>,
  mobs: Record<string, MobView>,
  selfSide: string | null,
): TargetView | null {
  if (!id) return null;
  if (id.startsWith('mob:')) {
    const m = mobs[id];
    if (m) {
      return {
        id: m.id,
        name: mobDisplayName(m),
        level: m.level,
        hp: m.hp,
        hpMax: Math.max(1, m.hpMax),
        resource: 0,
        resourceMax: 0,
        reaction: reactionFor(m, false, selfSide),
        classId: null,
        isPlayer: false,
      };
    }
  }
  const p = players[id];
  if (p) {
    return {
      id: p.id,
      name: p.name || 'Безымянный',
      level: p.level,
      hp: p.hp,
      hpMax: Math.max(1, p.hpMax),
      resource: p.resource,
      resourceMax: Math.max(1, p.resourceMax),
      reaction: reactionFor(p, true, selfSide),
      classId: p.classId,
      isPlayer: true,
    };
  }
  const m = mobs[id];
  if (m) {
    return {
      id: m.id,
      name: mobDisplayName(m),
      level: m.level,
      hp: m.hp,
      hpMax: Math.max(1, m.hpMax),
      resource: 0,
      resourceMax: 0,
      reaction: reactionFor(m, false, selfSide),
      classId: null,
      isPlayer: false,
    };
  }
  return null;
}

interface PortraitProps {
  /**
   * CSS dimension. Pass a token string (e.g. `var(--aiwari-portrait)`)
   * or a `<number>px` literal — both are spread directly into width/height.
   */
  size: number | string;
  classId: string | null;
  reaction: Reaction;
  isPlayer: boolean;
}

function Portrait({ size, classId, reaction, isPlayer }: PortraitProps) {
  const tint = (classId && CLASS_TINT[classId]) || (isPlayer ? '#4a4a4a' : '#3a2a1a');
  const ringStyle: CSSProperties = {
    width: size,
    height: size,
    borderRadius: '50%',
    background: `radial-gradient(circle at 35% 30%, ${tint} 0%, hsl(0 0% 5%) 95%)`,
    border: `2px solid ${REACTION_RING[reaction]}`,
    boxShadow: REACTION_GLOW[reaction],
    flexShrink: 0,
    position: 'relative',
  };
  return <div style={ringStyle} aria-hidden />;
}

interface TotMiniProps {
  view: TargetView;
}

function TotMini({ view }: TotMiniProps) {
  const hpPct = Math.max(0, Math.min(1, view.hp / view.hpMax));
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 2,
        marginLeft: 6,
        minWidth: 56,
      }}
    >
      <Portrait size={'var(--aiwari-portrait-sm)'} classId={view.classId} reaction={view.reaction} isPlayer={view.isPlayer} />
      <div
        className="aiwari-bar aiwari-bar-3d"
        style={{ height: 'var(--aiwari-bar-h-thin)', width: 56 }}
        aria-label={`ToT HP ${Math.round(view.hp)}/${Math.round(view.hpMax)}`}
      >
        <div
          className="aiwari-bar-fill"
          style={{ width: `${hpPct * 100}%`, background: 'var(--aiwari-bar-hp)' }}
        />
      </div>
      <span
        style={{
          fontFamily: 'var(--aiwari-font-body)',
          fontSize: 'var(--aiwari-text-xs)',
          lineHeight: 1.1,
          color: 'var(--aiwari-fg-muted)',
          maxWidth: 56,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
        title={view.name}
      >
        {view.name}
      </span>
    </div>
  );
}

/** Detect coarse mobile/touch viewport. Mirrors PlayerFrame conventions. */
function useIsMobile(): boolean {
  const force = useUiSettings((s) => s.forceMobileUi);
  if (force) return true;
  if (typeof window === 'undefined') return false;
  return window.innerWidth < 768;
}

export function TargetFrame() {
  const targetId = useTarget((s) => s.targetId);
  const totId = useTarget((s) => s.totId);
  const clearTarget = useTarget((s) => s.clear);
  const setMouseover = useTarget((s) => s.setMouseover);

  const players = useNet((s) => s.players);
  const mobs = useNet((s) => s.mobs);
  const selfId = useNet((s) => s.selfId);
  const selfSide = selfId ? players[selfId]?.side ?? null : null;

  const isMobile = useIsMobile();

  const onClick = useCallback(
    (e: MouseEvent<HTMLDivElement>) => {
      // Click on the frame surface (not on a child button/link) clears the
      // target, mirroring the WoW behaviour where clicking your target
      // frame's empty area unsets the target.
      if (e.target === e.currentTarget) {
        clearTarget();
      }
    },
    [clearTarget],
  );

  const onContextMenu = useCallback(
    (e: MouseEvent<HTMLDivElement>) => {
      // TODO: open the cast-on-target macro picker. For now we just clear
      // mouseover so a stale tooltip doesn't linger over the frame.
      e.preventDefault();
      setMouseover(null);
    },
    [setMouseover],
  );

  const target = resolveTarget(targetId, players, mobs, selfSide);
  const tot = resolveTarget(totId, players, mobs, selfSide);

  if (!target) return null;

  const hpPct = Math.max(0, Math.min(1, target.hp / target.hpMax));
  const resPct =
    target.resourceMax > 0
      ? Math.max(0, Math.min(1, target.resource / target.resourceMax))
      : 0;

  const wrapStyle: CSSProperties = isMobile
    ? {
        position: 'fixed',
        top: 110, // sits just below the player frame on mobile
        left: 16,
        zIndex: 10,
        width: 260,
        transform: 'scale(0.85)',
        transformOrigin: 'top left',
      }
    : {
        position: 'fixed',
        top: 16,
        left: 320,
        zIndex: 10,
        width: 280,
      };

  return (
    <div style={wrapStyle} onClick={onClick} onContextMenu={onContextMenu}>
      <SirusFrame size="sm">
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
          <Portrait
            size={'var(--aiwari-portrait)'}
            classId={target.classId}
            reaction={target.reaction}
            isPlayer={target.isPlayer}
          />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                marginBottom: 4,
              }}
            >
              <span
                style={{
                  fontFamily: 'var(--aiwari-font-display)',
                  fontSize: 'var(--aiwari-text-md)',
                  color: 'var(--aiwari-fg)',
                  flex: 1,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
                title={target.name}
              >
                {target.name}
              </span>
              <span
                className="aiwari-level-pill"
                title={`Уровень ${target.level}`}
              >
                {target.level}
              </span>
              <span
                style={{
                  width: 14,
                  height: 14,
                  borderRadius: '50%',
                  background:
                    (target.classId && CLASS_TINT[target.classId]) ||
                    (target.isPlayer ? '#4a4a4a' : '#3a2a1a'),
                  border: '1px solid var(--aiwari-frame-edge)',
                  flexShrink: 0,
                }}
                title={target.isPlayer ? target.classId ?? 'игрок' : 'существо'}
                aria-hidden
              />
            </div>
            <div className="aiwari-bar aiwari-bar-3d" style={{ height: 'var(--aiwari-bar-h)', marginBottom: 3 }}>
              <div
                className="aiwari-bar-fill"
                style={{ width: `${hpPct * 100}%`, background: 'var(--aiwari-bar-hp)' }}
              />
              <span className="aiwari-bar-text" style={{ lineHeight: 1.2, fontSize: 'var(--aiwari-text-xs)' }}>
                {Math.max(0, Math.round(target.hp))} / {Math.round(target.hpMax)}
              </span>
            </div>
            {target.resourceMax > 0 ? (
              <div className="aiwari-bar aiwari-bar-3d" style={{ height: 'var(--aiwari-bar-h-thin)' }}>
                <div
                  className="aiwari-bar-fill"
                  style={{ width: `${resPct * 100}%`, background: 'var(--aiwari-bar-mana)' }}
                />
              </div>
            ) : null}
          </div>
          {tot ? <TotMini view={tot} /> : null}
        </div>
      </SirusFrame>
    </div>
  );
}
